import React, { useEffect, useMemo, useState } from 'react';
import { TimePicker } from 'antd';
import dayjs from 'dayjs';
import {
  ClockIcon,
  MinusCircleIcon,
  PlusIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import storeApi from '../../../../services/storeApi';
import { weekDaysEnum } from '../../../../data/constants';
import WebsiteCloseModal from './components/website-close-modal';
import {
  SectionHeading,
  ToggleSwitch,
  primaryButtonClass,
} from '../components/settings-ui';

const format = 'hh:mm a';

const groupByDay = (storadays = []) =>
  [...storadays]
    .sort((a, b) => a.day - b.day)
    .reduce((acc, curr) => {
      const found = acc.find((a) => a.day === curr.day);
      if (!found) {
        acc.push({ day: curr.day, hours: [curr] });
      } else {
        found.hours.push(curr);
      }
      return acc;
    }, []);

const toDayjs = (hour, min) => dayjs().hour(hour).minute(min).second(0);

const WorkingHoursTab = ({ storeDetail, checkUpdate }) => {
  const [days, setDays] = useState(() => groupByDay(storeDetail.storadays));
  const [loading, setLoading] = useState(false);
  const [dialogs, setDialogs] = useState({ websiteclose: false });

  useEffect(() => {
    setDays(groupByDay(storeDetail.storadays || []));
  }, [storeDetail.storadays]);

  const dayMap = useMemo(() => {
    const map = new Map();
    days.forEach((d) => map.set(d.day, d.hours));
    return map;
  }, [days]);

  const closed =
    storeDetail.websiteclosedat &&
    dayjs().isBefore(
      dayjs(storeDetail.websiteclosedat).tz(storeDetail.timezoneid),
    );

  const showPauseBanner =
    closed &&
    dayjs(storeDetail.websiteclosedat).tz(storeDetail.timezoneid).year() <=
      2500;

  const updateHours = (day, updater) => {
    setDays((prev) => {
      const next = prev.map((d) => ({
        ...d,
        hours: d.hours.map((h) => ({ ...h })),
      }));
      const entry = next.find((d) => d.day === day);
      const result = updater(entry, next);
      return result || next;
    });
  };

  const onClickDeleteHour = (day, hourIndex) => {
    setDays((prev) => {
      const next = prev
        .map((d) =>
          d.day === day
            ? { ...d, hours: d.hours.filter((_, i) => i !== hourIndex) }
            : d,
        )
        .filter((d) => d.hours.length > 0);
      return next;
    });
  };

  const onChangeHour = (day, hourIndex, type, value) => {
    if (!value) return;
    updateHours(day, (entry) => {
      if (!entry) return;
      entry.hours[hourIndex][`${type}hour`] = value.hour();
      entry.hours[hourIndex][`${type}min`] = value.minute();
    });
  };

  const onClickAddHour = (day) => {
    setDays((prev) => {
      const entry = prev.find((d) => d.day === day);
      const newSlot = {
        starthour: 10,
        startmin: 0,
        endhour: 22,
        endmin: 0,
      };
      if (!entry) {
        return [...prev, { day, hours: [newSlot] }].sort(
          (a, b) => a.day - b.day,
        );
      }
      return prev.map((d) =>
        d.day === day ? { ...d, hours: [...d.hours, newSlot] } : d,
      );
    });
  };

  const onClickSubmit = async () => {
    try {
      setLoading(true);
      const body = { storeid: storeDetail.id, days };
      await storeApi.upsertHours({ storeid: storeDetail.id, days: [] });
      const res = await storeApi.upsertHours(body);
      if (res.status === 200) {
        checkUpdate(true);
      }
    } catch (error) {
      checkUpdate(false);
    } finally {
      setLoading(false);
    }
  };

  const onChangeWebsiteCloseDate = async (val) => {
    const form = {
      ...storeDetail,
      websiteclosedat: val || '',
      apartment: storeDetail?.apartment || '',
    };
    try {
      setLoading(true);
      const formData = new FormData();
      for (const key of Object.keys(form)) {
        formData.append(key, form[key]);
      }
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const res = await storeApi.upsert(formData, config);
      if (res.status === 200) {
        checkUpdate(true);
      }
    } catch (error) {
      checkUpdate(false);
    } finally {
      setLoading(false);
    }
  };

  const onToggleWebsite = async () => {
    if (closed) {
      await onChangeWebsiteCloseDate('');
    } else {
      setDialogs((prev) => ({ ...prev, websiteclose: true }));
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Working Hours"
        description="Control when this store accepts online orders and schedule temporary pauses."
      />

      <div
        className={[
          'rounded-md border p-5 shadow-sm transition',
          closed
            ? 'border-rose-200 bg-rose-50/60 dark:border-rose-900/50 dark:bg-rose-950/20'
            : 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/20',
        ].join(' ')}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div
              className={[
                'flex h-11 w-11 shrink-0 items-center justify-center rounded-md',
                closed
                  ? 'bg-rose-500/15 text-rose-600 dark:bg-rose-400/10 dark:text-rose-300'
                  : 'bg-emerald-500/15 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-300',
              ].join(' ')}
            >
              <ClockIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  Online orders
                </h3>
                <span
                  className={[
                    'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider',
                    closed
                      ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-200'
                      : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'h-1.5 w-1.5 rounded-full',
                      closed ? 'bg-rose-500' : 'bg-emerald-500',
                    ].join(' ')}
                  />
                  {closed ? 'Paused' : 'Open'}
                </span>
              </div>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {showPauseBanner ? (
                  <>
                    Online orders are paused until{' '}
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {dayjs(storeDetail.websiteclosedat)
                        .tz(storeDetail.timezoneid)
                        .format('MMM DD, h:mm A')}
                    </span>
                    .
                  </>
                ) : (
                  'Toggle to pause online orders temporarily.'
                )}
              </p>
            </div>
          </div>
          <ToggleSwitch
            checked={!closed}
            tone={closed ? 'rose' : 'emerald'}
            disabled={loading}
            onChange={onToggleWebsite}
          />
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900/60">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Weekly schedule
          </h3>
          <span className="text-xs font-medium text-slate-400 dark:text-slate-500">
            Local time
          </span>
        </div>

        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {weekDaysEnum.map((weekDay) => {
            const dayHours = dayMap.get(weekDay.value) || [];
            return (
              <li
                key={weekDay.value}
                className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:gap-6"
              >
                <div className="sm:w-32 sm:pt-2">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {weekDay.label}
                  </p>
                  {dayHours.length === 0 && (
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                      Closed
                    </p>
                  )}
                </div>
                <div className="flex flex-1 flex-wrap items-center gap-3">
                  {dayHours.map((hour, hourIndex) => (
                    <div
                      key={hourIndex}
                      className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 dark:bg-slate-800/60"
                    >
                      <TimePicker
                        needConfirm={false}
                        use12Hours
                        size="middle"
                        allowClear={false}
                        value={toDayjs(hour.starthour, hour.startmin)}
                        format={format}
                        onChange={(val) =>
                          onChangeHour(weekDay.value, hourIndex, 'start', val)
                        }
                      />
                      <span className="text-sm text-slate-400 dark:text-slate-500">
                        →
                      </span>
                      <TimePicker
                        needConfirm={false}
                        use12Hours
                        size="middle"
                        allowClear={false}
                        value={toDayjs(hour.endhour, hour.endmin)}
                        format={format}
                        onChange={(val) =>
                          onChangeHour(weekDay.value, hourIndex, 'end', val)
                        }
                      />
                      <button
                        type="button"
                        onClick={() =>
                          onClickDeleteHour(weekDay.value, hourIndex)
                        }
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-rose-500 transition hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400"
                        aria-label="Remove hours"
                      >
                        <MinusCircleIcon
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => onClickAddHour(weekDay.value)}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 text-xs font-semibold text-slate-600 transition hover:border-slate-400 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-300 dark:hover:border-slate-500 dark:hover:bg-slate-800"
                  >
                    <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Add hours
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="mt-5 flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2.5 text-xs leading-relaxed text-slate-600 dark:border-sky-500/20 dark:bg-sky-500/5 dark:text-slate-300">
          <InformationCircleIcon
            className="mt-0.5 h-4 w-4 shrink-0 text-blue-500 dark:text-sky-400"
            aria-hidden="true"
          />
          <p>
            These hours apply to{' '}
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              online ordering
            </span>{' '}
            only. They don&apos;t control when the kiosk turns on or off.
          </p>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={onClickSubmit}
          disabled={loading}
          className={primaryButtonClass}
        >
          {loading ? 'Saving…' : 'Save hours'}
        </button>
      </div>

      <WebsiteCloseModal
        show={dialogs.websiteclose}
        onClose={() => setDialogs((prev) => ({ ...prev, websiteclose: false }))}
        storeDetail={storeDetail}
        checkUpdate={checkUpdate}
      />
    </div>
  );
};

export default WorkingHoursTab;
